import { Timestamp } from 'firebase/firestore';
import { useMemo } from 'react';

import { ConsultationMode } from '../enums';
import { Filter, ReservationFilters } from '../types';
import { useCurrentUser } from './useCurrentUser';
import { useCurrentUserRoles } from './useCurrentUserRoles';

export function useReservationFilters(options: {
  canViewCompanyReservations?: boolean;
  filters: ReservationFilters;
  mode: ConsultationMode;
}) {
  const { canViewCompanyReservations, filters, mode } = options;
  const user = useCurrentUser();
  const { isAdmin, isAgent, isCorporateUser } = useCurrentUserRoles();

  return useMemo(
    () =>
      [
        ...(!isAdmin &&
        (!canViewCompanyReservations || filters.showOnlyMyReservations)
          ? [['madeForId', '==', user.id]]
          : []),
        ...(isAgent &&
        canViewCompanyReservations &&
        !filters.showOnlyMyReservations
          ? [['agencyId', '==', user.companyRef.id]]
          : []),
        ...(isCorporateUser &&
        canViewCompanyReservations &&
        !filters.showOnlyMyReservations
          ? [['corporateCompanyId', '==', user.companyRef.id]]
          : []),
        ...(!isCorporateUser &&
        canViewCompanyReservations &&
        !filters.showOnlyMyReservations
          ? [['companyId', '==', user.companyRef.id]]
          : []),
        ...(filters.companyId ? [['companyId', '==', filters.companyId]] : []),
        ...(filters.partNumber
          ? [['reel.normalizedCableId', '==', filters.partNumber]]
          : []),
        ...(filters.reservationNumber
          ? [['reservationNumber', '==', filters.reservationNumber]]
          : []),
        ...(mode === ConsultationMode.History &&
        filters.startDate &&
        filters.startDate.isValid
          ? [
              [
                'reservedTime',
                '>=',
                Timestamp.fromMillis(
                  filters.startDate.setZone(user.timeZone).toMillis()
                ),
              ],
            ]
          : []),
        ...(mode === ConsultationMode.History &&
        filters.endDate &&
        filters.endDate.isValid
          ? [
              [
                'reservedTime',
                '<=',
                Timestamp.fromMillis(
                  filters.endDate
                    .setZone(user.timeZone)
                    .plus({ days: 1 })
                    .toMillis()
                ),
              ],
            ]
          : []),
      ] as Filter[],
    [
      canViewCompanyReservations,
      filters,
      mode,
      isAdmin,
      isAgent,
      isCorporateUser,
      user,
    ]
  );
}
